import type {Employee} from 'client';
import {t} from 'i18next';

/** How the person on the profile relates to the viewer; the Ask questions follow from it. */
export type ProfileRelation = 'self' | 'manager' | 'above' | 'report' | 'peer' | 'org' | 'far';

type Person = Pick<Employee, 'ldap'>;

/** The viewer's side of the relation, from ``loggedInEmployeeAtom``. */
export type ProfileViewer = {
  employee: Person;
  /** The viewer first, then their manager, skip-level, and up. */
  reportingLine: Person[];
  teamMembers: Person[];
  directReports: Person[];
};

const includes = (people: Person[], ldap: string) => people.some((p) => p.ldap === ldap);

/**
 * Self, your manager, higher up your chain, your report, a peer (same
 * manager), someone in your org (their chain includes your skip-level or the
 * level above it), or someone far away. `personReportingLine` is the
 * profile's own line, the person first.
 */
export const profileRelation = ({
  person,
  personReportingLine,
  viewer,
}: {
  person: Person;
  personReportingLine: Person[];
  viewer: ProfileViewer;
}): ProfileRelation => {
  const {ldap} = person;
  const {reportingLine} = viewer;
  if (ldap === viewer.employee.ldap) return 'self';
  if (reportingLine[1]?.ldap === ldap) return 'manager';
  if (includes(reportingLine.slice(2), ldap)) return 'above';
  if (includes(viewer.directReports, ldap)) return 'report';
  if (includes(viewer.teamMembers, ldap)) return 'peer';
  if (reportingLine.slice(2, 4).some((boss) => includes(personReportingLine, boss.ldap))) {
    return 'org';
  }
  return 'far';
};

/**
 * Which chip a question came from: one about the person, one about their team
 * or work. The server prefetches for it (``profile_ask``) and works out the
 * relation itself.
 */
export type ProfileAskKind = 'person' | 'team';

/**
 * `label` is the chip text, with the first name. `question` is what gets
 * asked, with `@Full Name`, so the question bubble draws the person as a
 * mention pill and the asker can see who Ask was told about.
 * `suggestionKind` is what analytics logs (never a name).
 */
export type ProfileAskQuestion = {
  kind: ProfileAskKind;
  suggestionKind: `${ProfileAskKind}:${ProfileRelation}`;
  label: string;
  question: string;
};

// The team chip asks about the team by name for people further away; closer
// in, it asks about the person's own team or work.
const TEAM_BY_NAME: ReadonlySet<ProfileRelation> = new Set(['above', 'org', 'far']);

/**
 * One question about the person and one about their team, shaped by how they
 * relate to the viewer. `team` is the profile's main team; for people further
 * away the team question names it, so without one only the person question
 * is offered.
 */
export const profileAskQuestions = (
  name: string,
  relation: ProfileRelation,
  team?: string | null
): ProfileAskQuestion[] => {
  const fullName = name.trim().replace(/\s+/g, ' ');
  const firstName = fullName.split(' ')[0];
  if (!firstName || relation === 'self') {
    return [];
  }
  const mention = `@${fullName}`;
  const personKey = `profile_ask_person_${relation}`;
  const questions: ProfileAskQuestion[] = [
    {
      kind: 'person',
      suggestionKind: `person:${relation}`,
      label: t(personKey, {name: firstName}),
      question: t(personKey, {name: mention}),
    },
  ];
  const teamKey = `profile_ask_team_${relation}`;
  const teamName = team?.trim();
  if (TEAM_BY_NAME.has(relation)) {
    if (teamName) {
      const text = t(teamKey, {team: teamName});
      questions.push({
        kind: 'team',
        suggestionKind: `team:${relation}`,
        label: text,
        question: text,
      });
    }
  } else {
    questions.push({
      kind: 'team',
      suggestionKind: `team:${relation}`,
      label: t(teamKey, {name: firstName}),
      question: t(teamKey, {name: mention}),
    });
  }
  return questions;
};
